import { createElement, lazy, type ComponentType } from "react"
import { createRoute, type AnyRoute, type AnyRouter } from "@tanstack/react-router"

import type { LocalizedMessage } from "../localization/localized-message"
import { getApplicationPluginRouteId } from "./application-plugin-route"
import type { RoutedDocumentLocation } from "./routed-documents"

const applicationRouteData = Symbol("florisoft.application-route-data")

export type ApplicationRouteText = string | LocalizedMessage

export interface ApplicationDocumentRouteMatch<
  TParams = Readonly<Record<string, string>>,
  TSearch = unknown,
  TLoaderData = unknown,
> {
  readonly routeId: string
  readonly pathname: string
  readonly href: string
  readonly params: TParams
  readonly search: TSearch
  readonly loaderData: TLoaderData
}

type ApplicationDocumentRouteMatchFor<TRoute extends AnyRoute> = ApplicationDocumentRouteMatch<
  TRoute["types"]["allParams"],
  TRoute["types"]["fullSearchSchema"],
  TRoute["types"]["loaderData"]
>

export type ApplicationRouteContent =
  | {
      readonly kind: "page"
      readonly component: ComponentType
    }
  | {
      readonly kind: "document"
      readonly component: ComponentType
      readonly getDocumentKey?: (match: ApplicationDocumentRouteMatch) => string
      readonly getDocumentTitle?: (match: ApplicationDocumentRouteMatch) => ApplicationRouteText
    }

export interface ApplicationRouteNavigation {
  readonly icon?: string
  readonly group?: ApplicationRouteText
  /** Icon of the route's navigation group; the first route of a group that sets one wins. */
  readonly groupIcon?: string
  readonly order?: number
  /** Short status shown next to the navigation item, such as "Planned" or "New". */
  readonly badge?: ApplicationRouteText
}

export interface ApplicationRouteData {
  readonly title: ApplicationRouteText
  readonly description?: ApplicationRouteText
  readonly content: ApplicationRouteContent
  readonly navigation?: ApplicationRouteNavigation
}

export interface ApplicationRouteEntry extends ApplicationRouteData {
  readonly routeId: string
  readonly path: string
  /** The plugin whose routes contain this route; signed-in navigation shows it only while that plugin is authorized. */
  readonly pluginId?: string
}

export interface ApplicationDocumentDescriptor<
  TParams = Readonly<Record<string, string>>,
  TSearch = unknown,
  TLoaderData = unknown,
> {
  readonly route: ApplicationRouteEntry
  readonly routeId: string
  readonly key: string
  readonly location: string
  readonly pathname: string
  readonly params: TParams
  readonly search: TSearch
  readonly loaderData: TLoaderData
  readonly title: ApplicationRouteText
}

interface ApplicationRouteStaticData {
  readonly [applicationRouteData]?: ApplicationRouteData
}

interface CreateApplicationRouteOptions {
  readonly parentRoute: AnyRoute
  readonly path: string
  readonly title: ApplicationRouteText
  readonly description?: ApplicationRouteText
  readonly navigation?: ApplicationRouteNavigation
  readonly staticData?: Record<string, unknown>
}

interface ExistingApplicationRouteOptions<TRoute extends AnyRoute> {
  readonly route: TRoute
  readonly title: ApplicationRouteText
  readonly description?: ApplicationRouteText
  readonly navigation?: ApplicationRouteNavigation
}

type DocumentComponentOptions =
  | { readonly component: ComponentType; readonly loadComponent?: never }
  | { readonly component?: never; readonly loadComponent: () => Promise<{ default: ComponentType }> }

type ExistingApplicationDocumentRouteOptions<TRoute extends AnyRoute> = ExistingApplicationRouteOptions<TRoute> &
  DocumentComponentOptions & {
    readonly getDocumentKey?: (match: ApplicationDocumentRouteMatchFor<TRoute>) => string
    readonly getDocumentTitle?: (match: ApplicationDocumentRouteMatchFor<TRoute>) => ApplicationRouteText
  }

type SimpleApplicationDocumentRouteOptions = CreateApplicationRouteOptions &
  DocumentComponentOptions & {
    readonly getDocumentKey?: (match: ApplicationDocumentRouteMatch) => string
    readonly getDocumentTitle?: (match: ApplicationDocumentRouteMatch) => ApplicationRouteText
  }

export function createApplicationPageRoute<TRoute extends AnyRoute>(
  options: ExistingApplicationRouteOptions<TRoute> & { readonly component: ComponentType }
): TRoute
export function createApplicationPageRoute(
  options: CreateApplicationRouteOptions & { readonly component: ComponentType }
): AnyRoute
export function createApplicationPageRoute(
  options: (ExistingApplicationRouteOptions<AnyRoute> | CreateApplicationRouteOptions) & {
    readonly component: ComponentType
  }
) {
  const { component } = options
  return createApplicationRoute(options, {
    kind: "page",
    component,
  })
}

export function createApplicationDocumentRoute<TRoute extends AnyRoute>(
  options: ExistingApplicationDocumentRouteOptions<TRoute>
): TRoute
export function createApplicationDocumentRoute(options: SimpleApplicationDocumentRouteOptions): AnyRoute
export function createApplicationDocumentRoute(
  options: ExistingApplicationDocumentRouteOptions<AnyRoute> | SimpleApplicationDocumentRouteOptions
) {
  const { getDocumentKey, getDocumentTitle } = options
  const component = options.loadComponent == null ? options.component : lazy(options.loadComponent)
  return createApplicationRoute(options, {
    kind: "document",
    component,
    getDocumentKey: getDocumentKey as ((match: ApplicationDocumentRouteMatch) => string) | undefined,
    getDocumentTitle: getDocumentTitle as ((match: ApplicationDocumentRouteMatch) => ApplicationRouteText) | undefined,
  })
}

export function getApplicationRouteData(staticData: unknown): ApplicationRouteData | null {
  return (staticData as ApplicationRouteStaticData | undefined)?.[applicationRouteData] ?? null
}

export function getApplicationRouteEntries(router: AnyRouter): readonly ApplicationRouteEntry[] {
  return Object.values(router.routesById)
    .map((route) => {
      const data = getApplicationRouteData(route.options.staticData)
      if (data == null) return null
      const pluginId = getOwningPluginId(route)
      return {
        ...data,
        routeId: route.id,
        path: route.fullPath,
        ...(pluginId == null ? {} : { pluginId }),
      }
    })
    .filter((entry): entry is ApplicationRouteEntry => entry != null)
}

/** The nearest plugin boundary above a route owns it; a nested plugin owns its own routes. */
function getOwningPluginId(route: AnyRoute): string | undefined {
  for (let current: AnyRoute | undefined = route; current != null; current = current.parentRoute) {
    const pluginId = getApplicationPluginRouteId(current.options.staticData)
    if (pluginId != null) return pluginId
  }
  return undefined
}

export function createApplicationDocumentDescriptor(
  route: ApplicationRouteEntry,
  location: RoutedDocumentLocation
): ApplicationDocumentDescriptor {
  if (location.routeId == null || location.routeId !== route.routeId) {
    throw new Error(`Application route mismatch for document location: ${location.href}`)
  }

  const match: ApplicationDocumentRouteMatch = {
    routeId: route.routeId,
    pathname: location.pathname,
    href: location.href,
    params: location.params,
    search: location.search,
    loaderData: location.loaderData,
  }
  const content = route.content
  const key =
    content.kind === "document"
      ? (content.getDocumentKey?.(match) ?? `${route.routeId}:${location.pathname}`)
      : route.routeId
  const title = content.kind === "document" ? (content.getDocumentTitle?.(match) ?? route.title) : route.title

  return {
    route,
    routeId: route.routeId,
    key,
    location: location.href,
    pathname: location.pathname,
    params: location.params,
    search: location.search,
    loaderData: location.loaderData,
    title,
  }
}

function createApplicationRoute(
  options: ExistingApplicationRouteOptions<AnyRoute> | CreateApplicationRouteOptions,
  content: ApplicationRouteContent
) {
  const route =
    "route" in options
      ? options.route
      : createRoute({
          getParentRoute: () => options.parentRoute,
          path: options.path,
        })
  const configuredStaticData = "route" in options ? route.options.staticData : options.staticData

  route.update({
    // A page works in an ordinary Outlet as well as in a document workspace.
    // Documents remain host-rendered because their instance has a retained lifetime.
    component: content.kind === "page" ? () => createElement(content.component) : undefined,
    staticData: {
      ...configuredStaticData,
      [applicationRouteData]: Object.freeze({
        title: options.title,
        description: options.description,
        content,
        navigation: options.navigation == null ? undefined : Object.freeze({ ...options.navigation }),
      } satisfies ApplicationRouteData),
    },
  })
  return route
}
